import { Compass, TriangleAlert } from 'lucide-react'
import { isRouteErrorResponse, useRouteError } from 'react-router'
import { AnchorButton, Button, LinkButton } from '../components/ui/button'
import { isApiError } from '../lib/api'
import { useTitle } from '../lib/title'

export function ErrorScreen({ icon, title, text, action }: { icon: React.ReactNode; title: string; text: string; action: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center px-4 py-16">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-5 flex size-14 items-center justify-center rounded-full bg-surface-2 text-muted [&>svg]:size-6">{icon}</div>
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-muted">{text}</p>
        <div className="mt-6 flex justify-center gap-2">{action}</div>
      </div>
    </div>
  )
}

export function NotFoundPage({ what = 'page' }: { what?: string }) {
  useTitle('Not found')
  return (
    <ErrorScreen
      icon={<Compass />}
      title={`This ${what} does not exist`}
      text="It may have been deleted, or the link is wrong. If someone sent it to you, ask them for a new one."
      action={
        <LinkButton to="/studios" variant="primary">
          Go to studios
        </LinkButton>
      }
    />
  )
}

/** Shown when a page crashes, so a render error never leaves a blank screen. */
export function RouteErrorPage() {
  const error = useRouteError()
  useTitle('Error')
  if ((isRouteErrorResponse(error) && error.status === 404) || isApiError(error, 404)) return <NotFoundPage />
  console.error(error)
  return (
    <ErrorScreen
      icon={<TriangleAlert />}
      title="Something broke on this page"
      text="Reloading usually helps. Recordings in progress keep uploading in the background once the studio is open again."
      action={
        <>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <AnchorButton href="/studios">Go to studios</AnchorButton>
        </>
      }
    />
  )
}

/** For pages whose main query failed: a 404 shows the not-found screen, anything else a retry. */
export function QueryError({ error, retry, what }: { error: unknown; retry: () => void; what: string }) {
  if (isApiError(error, 404)) return <NotFoundPage what={what} />
  return (
    <ErrorScreen
      icon={<TriangleAlert />}
      title={`Could not load this ${what}`}
      text={error instanceof Error ? error.message : 'Something went wrong.'}
      action={
        <Button variant="primary" onClick={retry}>
          Try again
        </Button>
      }
    />
  )
}
